<template>
    <div class="max-w-4xl mt-4 mb-4 mx-auto p-6 bg-white rounded-lg shadow-md">
      <div class="flex justify-between items-center mb-6">
        <h1 class="text-2xl font-bold text-[#035a76]">Promotion Processing</h1>
        <button @click="$emit('back')" class="text-gray-500 hover:text-gray-700">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
  
      <!-- Employee Summary -->
      <div class="bg-gray-50 p-4 rounded-md mb-6">
        <h3 class="text-lg font-medium mb-3">Employee Details</h3>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p class="text-sm text-gray-500">Name</p>
            <p class="font-medium">{{ employee.name }}</p>
          </div>
          <div>
            <p class="text-sm text-gray-500">Current Position</p>
            <p class="font-medium">{{ employee.position }}</p>
          </div>
          <div>
            <p class="text-sm text-gray-500">Current Section/Class</p>
            <p class="font-medium">{{ employee.section }} {{ employee.currentClass ? `(${employee.currentClass})` : '' }}</p>
          </div>
        </div>
      </div>
  
      <!-- Promotion Form -->
      <form @submit.prevent="submitForm" class="space-y-6">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label class="block text-sm font-medium text-gray-700">New Position Title*</label>
            <input v-model="form.newPosition" type="text" required
                   class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]">
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700">New Salary*</label>
            <div class="mt-1 relative rounded-md shadow-sm">
              <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span class="text-gray-500 sm:text-sm">GHS</span>
              </div>
              <input v-model.number="form.newSalary" type="number" step="0.01" required
                     class="focus:ring-[#035a76] focus:border-[#035a76] block w-full pl-12 pr-12 sm:text-sm border-gray-300 rounded-md py-2 px-3"
                     placeholder="0.00">
            </div>
          </div>
        </div>
  
        <div v-if="employee.department === 'academics'" class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label class="block text-sm font-medium text-gray-700">New Section</label>
            <select v-model="form.newSection"
                    class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]">
              <option value="">Same as current</option>
              <option value="nursery">Nursery</option>
              <option value="primary">Primary</option>
              <option value="secondary">Secondary</option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700">New Class Assignment</label>
            <select v-model="form.newClass"
                    class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]">
              <option value="">Same as current</option>
              <option v-if="form.newSection === 'nursery' || (!form.newSection && employee.section === 'nursery')" 
                      v-for="opt in ['Creche', 'KG 1', 'KG 2']" :value="opt">{{ opt }}</option>
              <option v-if="form.newSection === 'primary' || (!form.newSection && employee.section === 'primary')" 
                      v-for="n in 6" :value="`Class ${n}`">Class {{ n }}</option>
              <option v-if="form.newSection === 'secondary' || (!form.newSection && employee.section === 'secondary')" 
                      v-for="n in 3" :value="`Form ${n}`">Form {{ n }}</option>
            </select>
          </div>
        </div>
  
        <div>
          <label class="block text-sm font-medium text-gray-700">Effective Date*</label>
          <input v-model="form.effectiveDate" type="date" required
                 class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]">
        </div>
  
        <div>
          <label class="block text-sm font-medium text-gray-700">Promotion Reason*</label>
          <select v-model="form.reason" required
                  class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]">
            <option value="">Select reason</option>
            <option value="performance">Performance Based</option>
            <option value="qualification">Additional Qualification</option>
            <option value="experience">Years of Experience</option>
            <option value="restructure">School Restructuring</option>
            <option value="other">Other</option>
          </select>
          <input v-if="form.reason === 'other'" v-model="form.otherReason" type="text" required
                 class="mt-2 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]"
                 placeholder="Please specify">
        </div>
  
        <div>
          <label class="block text-sm font-medium text-gray-700">Additional Responsibilities</label>
          <textarea v-model="form.responsibilities" rows="3"
                    class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]"
                    placeholder="List any new responsibilities..."></textarea>
        </div>
  
        <div class="flex justify-end space-x-4">
          <button type="button" @click="$emit('back')"
                  class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#035a76]">
            Cancel
          </button>
          <button type="submit"
                  class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#035a76] hover:bg-[#035a76]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#035a76]">
            Process Promotion
          </button>
        </div>
      </form>
    </div>
  </template>
  
  <script setup>
  import { ref, onMounted } from 'vue';
  
  const props = defineProps({
    employeeData: Object,
    contractType: String
  });
  
  const emit = defineEmits(['back', 'submit']);
  
  const employee = props.employeeData || {
    name: 'Kwame Mensah',
    position: 'Mathematics Teacher',
    section: 'Secondary',
    currentClass: 'Form 2',
    department: 'Academics'
  };
  
  const form = ref({
    newPosition: '',
    newSalary: '',
    newSection: '',
    newClass: '',
    effectiveDate: '',
    reason: '',
    otherReason: '',
    responsibilities: ''
  });
  
  const submitForm = () => {
    emit('submit', {
      type: 'promotion',
      employeeId: employee.id,
      ...form.value
    });
  };
  
  onMounted(() => {
    // Set default effective date to today
    form.value.effectiveDate = new Date().toISOString().split('T')[0];
  });
  </script>